import { useState } from 'react';
import { Button, Modal, ModalClose, ModalContent, ModalTitle, Textarea } from '@/components/ui';
import { cn } from '@/utils/helpers';

const FEEDBACK_REASONS = [
  'Result is wrong',
  'Forgot earlier context',
  "Couldn't finish the task",
  'Other',
] as const;

type FeedbackRating = 'up' | 'down' | null;

function ThumbsUpIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M7 22V11M2 13v7a2 2 0 002 2h12.4a2 2 0 001.94-1.52l1.76-7A2 2 0 0018.16 11H14a1 1 0 01-1-1V5.5a2.5 2.5 0 00-5 0v.56a3 3 0 01-.78 2.02L5 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThumbsDownIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M17 2V13M22 11V4a2 2 0 00-2-2H7.6a2 2 0 00-1.94 1.52l-1.76 7A2 2 0 005.84 13H10a1 1 0 011 1v4.5a2.5 2.5 0 005 0v-.56a3 3 0 01.78-2.02L19 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FeedbackThanksIcon() {
  return (
    <svg className="feedback-thanks__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="10" fill="var(--color-success)" />
      <path
        d="M6 10.5L8.5 13L14 7.5"
        stroke="var(--color-text-inverse)"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ResponseFeedback() {
  const [rating, setRating] = useState<FeedbackRating>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [thumbPop, setThumbPop] = useState<'up' | 'down' | null>(null);

  const handleThumbsUp = () => {
    if (submitted) return;
    setThumbPop('up');
    setRating('up');
    setModalOpen(false);
    setSelectedReason(null);
    setComment('');
    setSubmitted(true);
    setShowThanks(true);
  };

  const handleThumbsDown = () => {
    if (submitted) return;
    setThumbPop('down');
    setRating('down');
    setModalOpen(true);
  };

  const clearThumbPop = () => setThumbPop(null);

  const handleCancel = () => {
    setModalOpen(false);
    if (!submitted) {
      setRating(null);
      setSelectedReason(null);
      setComment('');
    }
  };

  const handleSubmit = () => {
    setModalOpen(false);
    setSubmitted(true);
    setRating('down');
    setShowThanks(true);
  };

  return (
    <>
      <div className="success-card__feedback-row">
        <div className="response-feedback" role="group" aria-label="Rate this response">
          <button
            type="button"
            className={cn(
              'response-feedback__btn',
              'response-feedback__btn--up',
              rating === 'up' && 'response-feedback__btn--active',
              thumbPop === 'up' && 'response-feedback__btn--pop'
            )}
            aria-label="Helpful"
            aria-pressed={rating === 'up'}
            disabled={submitted && rating !== 'up'}
            onClick={handleThumbsUp}
          >
            <span
              className="response-feedback__icon"
              aria-hidden
              onAnimationEnd={(e) => {
                if (e.animationName === 'feedback-thumb-up') clearThumbPop();
              }}
            >
              <ThumbsUpIcon />
            </span>
          </button>
          <button
            type="button"
            className={cn(
              'response-feedback__btn',
              'response-feedback__btn--down',
              rating === 'down' && 'response-feedback__btn--active',
              thumbPop === 'down' && 'response-feedback__btn--pop'
            )}
            aria-label="Not helpful"
            aria-pressed={rating === 'down'}
            disabled={submitted && rating !== 'down'}
            onClick={handleThumbsDown}
          >
            <span
              className="response-feedback__icon"
              aria-hidden
              onAnimationEnd={(e) => {
                if (e.animationName === 'feedback-thumb-down') clearThumbPop();
              }}
            >
              <ThumbsDownIcon />
            </span>
          </button>
        </div>

        {showThanks ? (
          <div className="feedback-thanks flow-thread__reveal" role="status" aria-live="polite">
            <FeedbackThanksIcon />
            <span className="feedback-thanks__text">Thanks for your feedback!</span>
            <button
              type="button"
              className="feedback-thanks__close"
              aria-label="Dismiss"
              onClick={() => setShowThanks(false)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>

      <Modal open={modalOpen} onOpenChange={(open) => !open && handleCancel()}>
        <ModalContent className="ui-modal ui-modal--response-feedback">
          <div className="ui-modal__header">
            <ModalTitle className="ui-modal__title">Help us improve</ModalTitle>
            <ModalClose className="ui-modal__close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </ModalClose>
          </div>

          <p className="feedback-modal__prompt">
            Please tell us why this response wasn&apos;t helpful for you
          </p>

          <div className="feedback-modal__reasons" role="group" aria-label="Reason">
            {FEEDBACK_REASONS.map((reason) => (
              <button
                key={reason}
                type="button"
                className={cn(
                  'feedback-modal__reason',
                  selectedReason === reason && 'feedback-modal__reason--selected'
                )}
                aria-pressed={selectedReason === reason}
                onClick={() => setSelectedReason(reason)}
              >
                {reason}
              </button>
            ))}
          </div>

          <Textarea
            className="feedback-modal__comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder=""
            aria-label="Additional feedback"
            rows={5}
          />

          <div className="ui-modal__footer">
            <div className="ui-modal__footer-right">
              <ModalClose asChild>
                <Button type="button" variant="secondary" size="lg" onClick={handleCancel}>
                  Cancel
                </Button>
              </ModalClose>
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={!selectedReason}
              >
                Submit
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}
